import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { SeoBreadcrumb } from "@/components/SeoBreadcrumb";
import { ArticleShare } from "@/components/ArticleShare";
import { getArticleBySlug, getRelatedArticles, getAdjacentArticles, Article } from "@/data/articles";
import { getBlogCategoryForArticle } from "@/data/blogCategories";
import { ArrowLeft, ArrowRight, Clock, Calendar, Tag, User, X } from "lucide-react";

const parseTableCells = (row: string): string[] => {
  return row.split('|').map(c => c.trim()).filter((_, i, arr) => i > 0 && i < arr.length);
};

const isSeparatorRow = (row: string): boolean => /^\|[\s\-:|]+\|$/.test(row.trim());

const renderContent = (content: string) => {
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;
  let listItems: string[] = [];
  let tableRows: string[] = [];
  let key = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${key++}`} className="space-y-2 mb-6 ml-4">
          {listItems.map((item, idx) => (
            <li key={idx} className="font-body text-lg text-muted-foreground leading-relaxed flex items-start gap-3">
              <span className="w-2 h-2 rounded-full bg-gold mt-2.5 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  const flushTable = () => {
    if (tableRows.length === 0) return;
    const dataRows = tableRows.filter(r => !isSeparatorRow(r));
    if (dataRows.length === 0) { tableRows = []; return; }
    const headerCells = parseTableCells(dataRows[0]);
    const bodyRows = dataRows.slice(1);
    elements.push(
      <div key={`table-${key++}`} className="overflow-x-auto mb-6 rounded-lg border border-border">
        <table className="w-full border-collapse font-body text-base">
          <thead>
            <tr className="bg-secondary">
              {headerCells.map((cell, ci) => (
                <th key={ci} className="px-4 py-3 text-left text-foreground font-semibold border-b border-border whitespace-nowrap">
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, ri) => {
              const cells = parseTableCells(row);
              return (
                <tr key={ri} className="border-b border-border last:border-0">
                  {cells.map((cell, ci) => (
                    <td key={ci} className="px-4 py-3 text-muted-foreground">
                      {cell}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
    tableRows = [];
  };

  while (i < lines.length) {
    const line = lines[i];

    if (line.trimStart().startsWith('|')) {
      flushList();
      tableRows.push(line);
      i++;
      continue;
    }

    flushTable();

    if (line.startsWith('### ')) {
      flushList();
      const text = line.slice(4);
      elements.push(
        <h3 key={`h3-${key++}`} className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
          {text}
        </h3>
      );
    } else if (line.startsWith('## ')) {
      flushList();
      const text = line.slice(3);
      elements.push(
        <h2 key={`h2-${key++}`} className="font-display text-2xl md:text-3xl font-bold text-foreground mt-10 mb-4">
          {text}
        </h2>
      );
    } else if (/^(—|--)\s/.test(line)) {
      flushList();
      elements.push(
        <div key={`sig-${key++}`} className="mt-10 mb-6 flex flex-col items-end gap-2">
          <span className="h-px w-24 bg-gold" aria-hidden="true" />
          <p className="font-display text-xl italic text-foreground">
            {line.replace(/^(—|--)\s/, '')}
          </p>
        </div>
      );
    } else if (line.startsWith('- ')) {
      listItems.push(line.slice(2));
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      elements.push(
        <p key={`p-${key++}`} className="font-body text-lg text-muted-foreground leading-relaxed mb-6">
          {line}
        </p>
      );
    }

    i++;
  }

  flushTable();
  flushList();
  return elements;
};

const ArticleGallery = ({ images, title }: { images: string[]; title: string }) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const altFor = (idx: number) => `${title} — immagine ${idx + 1} di ${images.length}`;

  return (
    <>
      <div className="mt-10 mb-6">
        <h3 className="font-display text-xl font-semibold text-foreground mb-4">📸 Galleria fotografica</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {images.map((src, idx) => (
            <button
              key={idx}
              onClick={() => setLightboxIndex(idx)}
              aria-label={`Apri ${altFor(idx)} a schermo intero`}
              className="aspect-square overflow-hidden rounded-lg border border-border hover:border-gold/50 transition-colors cursor-pointer"
            >
              <img src={src} alt={altFor(idx)} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </button>
          ))}
        </div>
      </div>

      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`Galleria fotografica: ${title}`}
          onClick={() => setLightboxIndex(null)}
        >
          <button
            onClick={() => setLightboxIndex(null)}
            aria-label="Chiudi galleria"
            className="absolute top-4 right-4 text-white/80 hover:text-white z-50"
          >
            <X size={32} />
          </button>
          <button
            aria-label="Foto precedente"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-50"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + images.length) % images.length); }}
          >
            <ArrowLeft size={32} />
          </button>
          <img
            src={images[lightboxIndex]}
            alt={altFor(lightboxIndex)}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            aria-label="Foto successiva"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-50"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % images.length); }}
          >
            <ArrowRight size={32} />
          </button>
          <span className="absolute bottom-4 text-white text-sm bg-black/60 px-2 py-1 rounded" aria-live="polite">
            {lightboxIndex + 1} / {images.length}
          </span>
        </div>
      )}
    </>
  );
};


const ArticleDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = slug ? getRelatedArticles(slug, 6) : [];
  const { prev, next } = slug ? getAdjacentArticles(slug) : {};
  const currentCategory = article ? getBlogCategoryForArticle(article.category) : null;

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const blogCategory = getBlogCategoryForArticle(article.category);

  // Use the prerendered og-image URL when available (matches scripts/prerender-meta.ts).
  // Falls back to the generic site image when no gallery exists.
  const ogImageUrl = (() => {
    if (article.gallery && article.gallery.length > 0) {
      const firstSrc = article.gallery[0];
      const ext = (firstSrc.match(/\.(jpe?g|png|webp|avif)/i)?.[0] || ".jpg").toLowerCase();
      return `https://taekwondomattei.com/og-images/${article.slug}${ext}`;
    }
    return "https://taekwondomattei.com/og-home.jpg";
  })();

  return (
    <Layout>
      <Helmet>
        <title>{article.metaTitle}</title>
        <meta name="description" content={article.metaDescription} />
        <meta property="og:title" content={article.ogTitle} />
        <meta property="og:description" content={article.ogDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://taekwondomattei.com/blog/${article.slug}`} />
        <meta property="og:locale" content="it_IT" />
        <link rel="canonical" href={`https://taekwondomattei.com/blog/${article.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.ogTitle} />
        <meta name="twitter:description" content={article.ogDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
        {article.faqSchema && (
          <script type="application/ld+json">{article.faqSchema}</script>
        )}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.metaDescription,
          "author": { "@type": "Organization", "name": article.author || "Taekwondo Mattei Team" },
          "publisher": { "@type": "Organization", "name": "Taekwondo Mattei", "url": "https://taekwondomattei.com" },
          "datePublished": article.publishedAt,
          "mainEntityOfPage": `https://taekwondomattei.com/blog/${article.slug}`,
          "image": ogImageUrl
        })}</script>
      </Helmet>

      <SeoBreadcrumb items={[
        { label: "Blog", href: "/blog" },
        { label: article.title }
      ]} />

      {/* Hero */}
      <section className="py-10 md:py-16 bg-secondary">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gold hover:text-gold-light transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            <span className="font-body text-sm">Torna al blog</span>
          </Link>
          
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <Link
              to={`/blog/categoria/${blogCategory.slug}`}
              className="px-4 py-1.5 bg-gold/10 text-gold hover:bg-gold/20 text-sm font-medium rounded-full flex items-center gap-2 transition-colors"
            >
              <Tag size={14} />
              {blogCategory.label}
            </Link>
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock size={14} />
              {article.readTime} min di lettura
            </span>
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar size={14} />
              {new Date(article.publishedAt).toLocaleDateString("it-IT", {
                day: "numeric",
                month: "long",
                year: "numeric"
              })}
            </span>
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <User size={14} />
              {article.author || "Taekwondo Mattei Team"}
            </span>
          </div>
          
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
            {article.title}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16 bg-background">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <article className="max-w-none">
            {renderContent(article.content)}
          </article>

          {/* Share */}
          <ArticleShare title={article.title} excerpt={article.excerpt} />

          {/* Gallery */}
          {article.gallery && article.gallery.length > 0 && (
            <ArticleGallery images={article.gallery} title={article.title} />
          )}

          {/* CTA in article */}
          <div className="mt-12 p-8 bg-secondary rounded-2xl border border-gold/20">
            <h3 className="font-display text-xl font-semibold text-foreground mb-4">
              Vuoi vedere di persona?
            </h3>
            <p className="font-body text-muted-foreground mb-6">
              La prima lezione è gratuita. Vieni a scoprire il Taekwondo nella nostra palestra.
            </p>
            <Link to="/lezione-di-prova" className="btn-accent inline-flex">
              Prenota una lezione di prova
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>

          {/* Internal link back to home — anchor "Taekwondo a Roma" */}
          <p className="mt-8 font-body text-sm text-muted-foreground leading-relaxed">
            Scopri di più sulla nostra scuola di{" "}
            <Link to="/" className="text-gold underline underline-offset-2 hover:text-gold-light">
              Taekwondo a Roma
            </Link>{" "}
            — zona Portuense, Monteverde, Bravetta, Corviale e Trullo — o consulta la{" "}
            <Link to="/blog/costi-taekwondo-roma" className="text-gold underline underline-offset-2 hover:text-gold-light">
              guida ai costi dei corsi di Taekwondo a Roma
            </Link>
            .
          </p>


          {/* Prev / Next Navigation */}
          {(prev || next) && (
            <nav className="mt-12 pt-8 border-t border-border flex justify-between items-start gap-8">
              {prev ? (
                <Link
                  to={`/blog/${prev.slug}`}
                  className="group flex flex-col items-start gap-1 max-w-[45%]"
                >
                  <span className="font-body text-xs text-muted-foreground uppercase tracking-wider">
                    Articolo precedente
                  </span>
                  <span className="flex items-center gap-2 font-display text-base font-semibold text-foreground group-hover:text-gold transition-colors line-clamp-2">
                    <ArrowLeft size={16} className="flex-shrink-0" />
                    {prev.title}
                  </span>
                </Link>
              ) : (
                <div />
              )}
              {next ? (
                <Link
                  to={`/blog/${next.slug}`}
                  className="group flex flex-col items-end gap-1 max-w-[45%] text-right"
                >
                  <span className="font-body text-xs text-muted-foreground uppercase tracking-wider">
                    Articolo successivo
                  </span>
                  <span className="flex items-center gap-2 font-display text-base font-semibold text-foreground group-hover:text-gold transition-colors line-clamp-2">
                    {next.title}
                    <ArrowRight size={16} className="flex-shrink-0" />
                  </span>
                </Link>
              ) : (
                <div />
              )}
            </nav>
          )}
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-12 md:py-16 bg-secondary">
          <div className="container-wide mx-auto px-4 md:px-8">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground">
                Continua a leggere
              </h2>
              {currentCategory && (
                <Link
                  to={`/blog/categoria/${currentCategory.slug}`}
                  className="font-body text-sm font-medium text-gold inline-flex items-center gap-1 hover:gap-2 transition-all"
                >
                  Tutti gli articoli in “{currentCategory.shortLabel}”
                  <ArrowRight size={14} />
                </Link>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((relatedArticle) => (
                <Link
                  key={relatedArticle.id}
                  to={`/blog/${relatedArticle.slug}`}
                  className="group card-elevated border border-border p-6"
                >
                  <span className="px-3 py-1 bg-gold/10 text-gold text-xs font-medium rounded-full">
                    {getBlogCategoryForArticle(relatedArticle.category).shortLabel}
                  </span>
                  <h3 className="font-display text-lg font-semibold text-foreground mt-4 group-hover:text-gold transition-colors line-clamp-2">
                    {relatedArticle.title}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground mt-2 line-clamp-2">
                    {relatedArticle.excerpt}
                  </p>
                </Link>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link
                to="/blog"
                className="font-body text-sm font-medium text-foreground inline-flex items-center gap-1 hover:text-gold transition-colors"
              >
                Vedi tutti gli articoli del blog
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default ArticleDetail;
